import * as Sentry from '@sentry/react';
import { supabase } from '../lib/supabase';

const DEDUPE_WINDOW_MS = 15000;
const MAX_STR_LEN = 4000;
const recentFingerprints = new Map();

let globalHandlersInstalled = false;
let axiosInterceptorInstalled = false;

function trimString(value, max = MAX_STR_LEN) {
  const str = String(value ?? '');
  return str.length > max ? `${str.slice(0, max)}…` : str;
}

function safeJson(value, depth = 0) {
  if (value == null) return value;
  if (depth > 3) return '[max-depth]';
  if (typeof value === 'string') return trimString(value);
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (value instanceof Error) {
    return {
      name: value.name,
      message: trimString(value.message),
      stack: trimString(value.stack || '', 8000),
    };
  }
  if (Array.isArray(value)) return value.slice(0, 10).map((item) => safeJson(item, depth + 1));
  if (typeof value === 'object') {
    const out = {};
    for (const [key, entry] of Object.entries(value).slice(0, 20)) {
      out[key] = safeJson(entry, depth + 1);
    }
    return out;
  }
  return trimString(value);
}

function makeFingerprint(source, message, route, stack) {
  return `${source}::${route}::${message}::${(stack || '').slice(0, 300)}`;
}

function isDuplicate(fingerprint) {
  const now = Date.now();
  for (const [key, seenAt] of recentFingerprints.entries()) {
    if (now - seenAt > DEDUPE_WINDOW_MS) recentFingerprints.delete(key);
  }
  const lastSeen = recentFingerprints.get(fingerprint);
  if (lastSeen && now - lastSeen < DEDUPE_WINDOW_MS) return true;
  recentFingerprints.set(fingerprint, now);
  return false;
}

async function getSessionContext() {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    return {
      token: session?.access_token || '',
      user: session?.user
        ? { id: session.user.id, email: session.user.email || null }
        : null,
    };
  } catch {
    return { token: '', user: null };
  }
}

function buildContext(details = {}) {
  return {
    route: `${window.location.pathname}${window.location.search}`,
    href: window.location.href,
    referrer: document.referrer || null,
    language: navigator.language || null,
    userAgent: navigator.userAgent || null,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
    mode: import.meta.env.MODE || 'production',
    tier: window.localStorage.getItem('kairos_tier') || 'free',
    details: safeJson(details),
  };
}

export async function reportBrowserError({ source, error, message, details = {}, level = 'error' }) {
  try {
    const resolvedMessage = trimString(message || error?.message || 'Unknown browser error');
    const stack = trimString(error?.stack || '', 8000) || null;
    const route = `${window.location.pathname}${window.location.search}`;
    const fingerprint = makeFingerprint(source, resolvedMessage, route, stack || '');
    if (isDuplicate(fingerprint)) return;

    const context = buildContext(details);
    const { token, user } = await getSessionContext();
    const payload = {
      source,
      level,
      message: resolvedMessage,
      stack,
      userId: user?.id || null,
      userEmail: user?.email || null,
      ...context,
    };

    Sentry.captureException(error || new Error(resolvedMessage), {
      level,
      tags: { source },
      extra: payload,
    });

    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;

    await fetch(`${import.meta.env.VITE_API_BASE || ''}/api/browser-errors`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      keepalive: true,
      credentials: 'same-origin',
    }).catch(() => {});
  } catch {
    // Never let browser logging crash the app.
  }
}

export function installGlobalBrowserErrorHandlers() {
  if (globalHandlersInstalled) return;
  globalHandlersInstalled = true;

  window.addEventListener('error', (event) => {
    reportBrowserError({
      source: 'window.error',
      error: event.error,
      message: event.message,
      details: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    reportBrowserError({
      source: 'window.unhandledrejection',
      error: reason instanceof Error ? reason : undefined,
      message: reason?.message || String(reason || 'Unhandled promise rejection'),
      details: { reason: safeJson(reason) },
    });
  });
}

export function installAxiosBrowserErrorInterceptor(axiosInstance) {
  if (axiosInterceptorInstalled) return;
  axiosInterceptorInstalled = true;

  axiosInstance.interceptors.response.use(
    (response) => response,
    (err) => {
      const status = err?.response?.status;
      const url = String(err?.config?.url || '');
      const shouldReport = !url.includes('/api/browser-errors') && (
        err?.code === 'ERR_NETWORK' ||
        !status ||
        status >= 500
      );

      if (shouldReport) {
        reportBrowserError({
          source: 'axios',
          error: err,
          message: err?.message || 'API request failed',
          details: {
            code: err?.code || null,
            status: status || null,
            method: err?.config?.method || 'get',
            url,
            response: safeJson(err?.response?.data),
          },
        });
      }

      return Promise.reject(err);
    }
  );
}